import { Archive, Pencil, RotateCcw, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  useProjectQuery,
  useUpdateProjectMutation,
  type Project,
  type TrackCardQuery,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import {
  Banner,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  ResourceActionsMenu,
  StateSurface,
  Textarea,
} from '../../design-system/index.js';
import { PageHeader } from '../shared/page.js';
import { TrackCollection } from '../tracks/track-collection.js';
import { ProjectDeleteDialog } from './project-delete-dialog.js';

export function ProjectDetailPage() {
  const { id } = useParams();
  const { connection, library } = useWorkspace();
  const navigate = useNavigate();
  const project = useProjectQuery(id);
  const update = useUpdateProjectMutation();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const item = project.data;
  const trackQuery = useMemo<TrackCardQuery | null>(
    () =>
      id
        ? {
            connection_id: connection.id,
            library_id: library.id,
            composer_project_id: id,
            favorite: 'any',
            limit: 50,
          }
        : null,
    [connection.id, id, library.id],
  );
  const playbackContext = useMemo(
    () => ({
      kind: 'project' as const,
      id: `project:${id ?? 'unresolved'}`,
      label: item?.title ?? 'Project',
    }),
    [id, item?.title],
  );

  if (project.isLoading) return <StateSurface kind="loading" title="Loading project" />;
  if (project.error)
    return (
      <StateSurface
        kind="error"
        title="Project unavailable"
        action={
          <Button variant="secondary" onClick={() => void project.refetch()}>
            Try again
          </Button>
        }
      />
    );
  if (!item || !trackQuery) return <StateSurface title="Project not found" />;

  return (
    <div>
      <PageHeader
        title={
          <>
            {item.title}{' '}
            <span className="inline-flex align-middle">
              <ResourceActionsMenu
                orientation="vertical"
                label={`Actions for ${item.title}`}
                actions={[
                  {
                    label: item.status === 'active' ? 'Archive project' : 'Restore project',
                    icon:
                      item.status === 'active' ? (
                        <Archive aria-hidden="true" />
                      ) : (
                        <RotateCcw aria-hidden="true" />
                      ),
                    disabled: update.isPending,
                    onSelect: () =>
                      void update
                        .mutateAsync({
                          projectId: item.id,
                          body: {
                            title: item.title,
                            brief: item.brief,
                            notes: item.notes,
                            status: item.status === 'active' ? 'archived' : 'active',
                            expected_revision: item.revision,
                          },
                        })
                        .catch(() => undefined),
                  },
                  {
                    label: 'Delete project',
                    icon: <Trash2 aria-hidden="true" />,
                    destructive: true,
                    onSelect: () => setConfirmDelete(true),
                  },
                ]}
              />
            </span>
          </>
        }
        description={item.brief}
        action={
          <div className="flex flex-wrap gap-2">
            {item.status === 'active' ? (
              <Button asChild>
                <Link to={`/create?project=${item.id}`}>
                  <Sparkles />
                  Create now
                </Link>
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => setEditing(true)}>
              <Pencil />
              Edit
            </Button>
          </div>
        }
      />

      {item.status === 'archived' ? (
        <Banner className="mb-5" tone="warning" title="Project archived">
          Restore this project before creating another generation inside it.
        </Banner>
      ) : null}
      {update.error ? (
        <Banner className="mb-5" tone="danger" title="Project update failed">
          Refresh the project and try again.
        </Banner>
      ) : null}

      {item.notes ? (
        <section className="mb-6 rounded-lg border border-border bg-surface p-5">
          <h2 className="font-semibold text-ink">Notes</h2>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-ink-2">{item.notes}</p>
        </section>
      ) : null}

      <section aria-labelledby="project-songs-heading">
        <h2 className="sr-only" id="project-songs-heading">
          Songs
        </h2>
        <TrackCollection
          emptyDescription="Create a song in this project and it will appear here."
          emptyTitle="No songs in this project yet"
          errorTitle="Project songs could not be loaded"
          playbackContext={playbackContext}
          query={trackQuery}
        />
      </section>

      <EditProjectDialog project={item} open={editing} onOpenChange={setEditing} />
      <ProjectDeleteDialog
        open={confirmDelete}
        onDeleted={() => navigate('/projects', { replace: true })}
        onOpenChange={setConfirmDelete}
        project={item}
      />
    </div>
  );
}

function EditProjectDialog({
  project,
  open,
  onOpenChange,
}: {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateProjectMutation();
  const [title, setTitle] = useState(project.title);
  const [brief, setBrief] = useState(project.brief ?? '');
  const [notes, setNotes] = useState(project.notes ?? '');

  useEffect(() => {
    if (!open) return;
    setTitle(project.title);
    setBrief(project.brief ?? '');
    setNotes(project.notes ?? '');
  }, [open, project]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Edit project</DialogTitle>
        <form
          className="grid gap-4"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!title.trim()) return;
            try {
              await update.mutateAsync({
                projectId: project.id,
                body: {
                  title: title.trim(),
                  brief: brief.trim() || null,
                  notes: notes.trim() || null,
                  status: project.status,
                  expected_revision: project.revision,
                },
              });
              onOpenChange(false);
            } catch {
              // Mutation state keeps the dialog open with an actionable error.
            }
          }}
        >
          <Field>
            <FieldLabel htmlFor="edit-project-title">Title</FieldLabel>
            <Input
              id="edit-project-title"
              maxLength={160}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="edit-project-brief">Creative brief</FieldLabel>
            <Textarea
              id="edit-project-brief"
              rows={4}
              maxLength={10_000}
              value={brief}
              onChange={(event) => setBrief(event.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="edit-project-notes">Notes</FieldLabel>
            <Textarea
              id="edit-project-notes"
              rows={6}
              maxLength={100_000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Field>
          {update.error ? <FieldError>The project could not be updated.</FieldError> : null}
          <div className="flex justify-end">
            <Button type="submit" disabled={!title.trim() || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save project'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
